import React, { useState, useMemo } from 'react';
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  Radio,
  Select,
  MenuItem,
  CircularProgress,
} from '@material-ui/core';
import { Close as CloseIcon, ArrowBack as ArrowBackIcon, Check as CheckIcon } from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import { DateTime } from 'luxon';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { useEndpointFetch } from '../../../../../global/network/useEndpointFetch';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { FallbackAvatar } from '../../../../general/user-profile/components/Avatar';
import { MatchedCandidate } from './matching/MatchingPanel';
import { MatchingRule } from './matching/use-matching-rules';
import {
  SaveActivityFetchVariables,
  SaveActivityFetchSessionVariables,
} from '../../../activity-management/activity-form-v2/activity-form-actions-v2';

type SchedulingType = 'regular' | 'flexible';
type FrequencyType = 'weekly' | 'fortnightly' | 'monthly';

interface ApplicationActionsDialogProps {
  open: boolean;
  onClose: () => void;
  profileId: string;
  applicantName: string;
  applicantRoleId: string;
  counterpartRoleId: string;
  rule: MatchingRule;
  applicantPrograms: { programId: string; name: string }[];
  matchedCandidate: MatchedCandidate;
  onSuccess: () => void;
}

const useStyles = makeStyles({
  dialogPaper: {
    width: 520,
    maxHeight: '90vh',
    borderRadius: 8,
  },
  header: {
    padding: '16px 24px 0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerStep1: {
    padding: '8px 16px',
    borderBottom: '1px solid #DCDCDC',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: 23,
    fontWeight: 550,
    color: '#545454',
  },
  subtitle: {
    fontSize: 14,
    color: '#646464',
    padding: '2px 24px 0',
  },
  titleStep1: {
    fontSize: 16,
    fontWeight: 600,
    color: '#545454',
    display: 'flex',
    alignItems: 'center',
    '& > *:not(:first-child)': {
      marginLeft: 8,
    },
  },
  cardList: {
    padding: '20px 24px 24px',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  templateCard: {
    border: '2px solid #DCDCDC',
    borderRadius: 8,
    padding: '18px 20px',
    marginBottom: 16,
    '&:last-child': {
      marginBottom: 0,
    },
    cursor: 'pointer',
    background: '#fff',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'flex-start',
    '&:hover': {
      borderColor: '#9E9E9E',
    },
  },
  templateCardSelected: {
    borderColor: '#796fff',
    background: 'rgba(121, 111, 255, 0.05)',
  },
  cardIcon: {
    width: 40,
    height: 40,
    borderRadius: 8,
    background: '#F2F2F2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginRight: 16,
  },
  cardIconSelected: {
    background: '#796fff',
    '& svg': { fill: '#fff' },
  },
  cardText: {
    minWidth: 0,
    paddingRight: 12,
  },
  cardTitle: {
    fontWeight: 600,
    fontSize: '0.95rem',
    color: '#545454',
    lineHeight: 1.3,
    marginBottom: 1,
  },
  cardDesc: {
    fontSize: '0.8rem',
    color: '#646464',
    lineHeight: 1.4,
  },
  footer: {
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'flex-end',
    '& > *:not(:first-child)': {
      marginLeft: 8,
    },
  },
  footerBordered: {
    borderTop: '1px solid #DCDCDC',
  },
  step1Content: {
    padding: 24,
  },
  templateBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    background: '#F2F2F2',
    borderRadius: 4,
    padding: '4px 10px',
    marginBottom: 24,
    fontSize: '0.8rem',
    color: '#646464',
  },
  fieldLabel: {
    fontSize: '0.75rem',
    color: '#646464',
    marginBottom: 6,
    fontWeight: 500,
  },
  sectionLabel: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#545454',
    marginBottom: 8,
  },
  teamList: {
    border: '1px solid #DCDCDC',
    borderRadius: 8,
    overflow: 'hidden',
    marginBottom: 20,
  },
  teamRow: {
    padding: '12px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '& + &': {
      borderTop: '1px solid #DCDCDC',
    },
  },
  roleBadge: {
    fontSize: '0.75rem',
    fontWeight: 600,
    borderRadius: 4,
    padding: '2px 8px',
    color: '#646464',
    background: '#F2F2F2',
  },
  radioCard: {
    border: '2px solid #DCDCDC',
    borderRadius: 8,
    padding: '14px 16px',
    cursor: 'pointer',
    background: '#fff',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: '#9E9E9E',
    },
  },
  radioCardSelected: {
    borderColor: '#796fff',
    background: 'rgba(121, 111, 255, 0.05)',
  },
  sessionList: {
    border: '1px solid #DCDCDC',
    borderRadius: 8,
    overflow: 'hidden',
    marginBottom: 20,
  },
  sessionRow: {
    padding: '12px 16px',
    '& + &': {
      borderTop: '1px solid #DCDCDC',
    },
  },
  sessionName: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.9rem',
    fontWeight: 500,
    color: '#545454',
  },
  sessionReport: {
    fontSize: '0.8rem',
    color: '#646464',
    marginLeft: 24,
    marginTop: 4,
  },
  badge: {
    fontSize: '0.7rem',
    color: '#646464',
    background: '#F2F2F2',
    borderRadius: 4,
    padding: '1px 6px',
    fontWeight: 400,
  },
});

export const ApplicationActionsDialog: React.FC<ApplicationActionsDialogProps> = ({
  open,
  onClose,
  profileId,
  applicantName,
  applicantRoleId,
  counterpartRoleId,
  rule,
  applicantPrograms,
  matchedCandidate,
  onSuccess,
}) => {
  const classes = useStyles();
  const { theme } = useCampfireTheme();
  const { setSnackbar } = useSnackbar();
  const [step, setStep] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>('rule');
  const [activityName, setActivityName] = useState('');
  const [startDate, setStartDate] = useState(DateTime.local().toISODate());
  /**
   * A rule either pins a program or pairs within the applicant's own. In the
   * second case the activity goes in a program both people share, and the
   * coordinator picks when there is more than one.
   */
  const candidateProgramIds = matchedCandidate.programs.map((program) => program.programId);
  const eligiblePrograms = rule.program
    ? [rule.program]
    : applicantPrograms.filter((program) => candidateProgramIds.includes(program.programId));

  const [selectedProgramId, setSelectedProgramId] = useState<string>(
    eligiblePrograms.length > 0 ? eligiblePrograms[0].programId : ''
  );

  const [scheduling, setScheduling] = useState<SchedulingType>(rule.actionScheduling as SchedulingType);
  const [frequency, setFrequency] = useState<FrequencyType>(rule.actionFrequency as FrequencyType);
  const [endDate, setEndDate] = useState(
    DateTime.local()
      .plus({ months: rule.actionDurationMonths })
      .toISODate()
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const apiUrl = useApiUrl();
  const saveActivity = useEndpointFetch<SaveActivityFetchVariables, { activityId: string }>('/vm/activity/save', {
    formData: true,
  });
  const completeProfile = useEndpointFetch<{ profileId: string; bypass?: boolean }, string>(
    '/vm/volunteer/profile/complete'
  );
  const enrolFetch = useCampfireFetch({ defer: true });

  const isCounterpart = applicantRoleId === counterpartRoleId;
  const mentorName = isCounterpart ? matchedCandidate.name : applicantName;
  const participantName = isCounterpart ? applicantName : matchedCandidate.name;
  const mentorRoleName = isCounterpart ? rule.counterpartRole.name : rule.applicantRole.name;
  const participantRoleName = isCounterpart ? rule.applicantRole.name : rule.counterpartRole.name;

  const defaultActivityName = useMemo(() => {
    return `${mentorName} & ${participantName}`;
  }, [mentorName, participantName]);

  const handleContinueToStep1 = () => {
    if (!activityName) {
      setActivityName(defaultActivityName);
    }
    setStep(1);
  };

  const handleClose = () => {
    setStep(0);
    setSelectedTemplate('rule');
    setActivityName('');
    setStartDate(DateTime.local().toISODate());
    setScheduling(rule.actionScheduling as SchedulingType);
    setFrequency(rule.actionFrequency as FrequencyType);
    setEndDate(
      DateTime.local()
        .plus({ months: rule.actionDurationMonths })
        .toISODate()
    );
    onClose();
  };

  const buildRecurrences = () => {
    if (scheduling !== 'regular') return [];

    const weekInterval = frequency === 'weekly' ? 1 : frequency === 'fortnightly' ? 2 : 4;
    const startDt = DateTime.fromISO(startDate);
    const dayMap: Record<number, string> = { 1: 'MO', 2: 'TU', 3: 'WE', 4: 'TH', 5: 'FR', 6: 'SA', 7: 'SU' };
    const day = dayMap[startDt.weekday] || 'MO';

    return [
      {
        __typename: 'WeeklyRecurrenceType' as const,
        startDate,
        endDate,
        days: [day],
        weekInterval,
      },
    ];
  };

  const enrolVolunteer = (activityId: string, volunteerId: string) => {
    return enrolFetch.run({
      method: 'post',
      url: `${apiUrl}/vm/activity/enrolment/enrol`,
      data: {
        activityIds: [activityId],
        volunteerIds: [volunteerId],
      },
    });
  };

  const handleCreateMatch = async () => {
    if (!selectedProgramId) {
      setSnackbar({
        open: true,
        variant: 'error',
        message: 'This pair has no program in common. Add one to both volunteers first.',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const profileRes = await completeProfile.run({ profileId });
      if (!profileRes.ok) throw new Error('Failed to complete applicant profile');
      const vol1Data = profileRes.data?.data;
      const volunteerId1 = typeof vol1Data === 'string' ? vol1Data : vol1Data?.volunteerId;

      const matchedProfileRes = await completeProfile.run({ profileId: matchedCandidate.profileId, bypass: true });
      if (!matchedProfileRes.ok) throw new Error('Failed to complete matched candidate profile');
      const vol2Data = matchedProfileRes.data?.data;
      const volunteerId2 = typeof vol2Data === 'string' ? vol2Data : vol2Data?.volunteerId;

      const sessions: SaveActivityFetchSessionVariables[] =
        selectedTemplate === 'rule' && ruleSessions.length > 0
          ? ruleSessions.map((session, i) => ({
              name: session.name,
              description: '',
              startTime: null,
              endTime: null,
              reportTypeId: session.reportType?.reportTypeId ?? null,
              // The first session carries the activity's cadence; the rest are
              // one-off reviews the rule marks as flexible.
              isFlexible: i === 0 ? scheduling === 'flexible' : session.isFlexible,
            }))
          : [
              {
                name: 'Meeting',
                description: '',
                startTime: null,
                endTime: null,
                reportTypeId: null,
                isFlexible: scheduling === 'flexible',
              },
            ];

      const activityData: SaveActivityFetchVariables = {
        programId: selectedProgramId,
        name: activityName || defaultActivityName,
        description: '',
        startDate,
        endDate: scheduling === 'regular' ? endDate : undefined,
        isRestrictedActivity: false,
        isHidden: false,
        maxTeam: null,
        hasOpenRoster: false,
        requireRosterTemplateApproval: false,
        allowCICO: null,
        allowIndividualShiftTimes: null,
        allowRosterComments: null,
        sessions,
        recurrences: buildRecurrences(),
      };

      const activityRes = await saveActivity.run(activityData);
      if (!activityRes.ok) throw new Error('Failed to create activity');
      const activityId = activityRes.data?.data?.activityId;

      if (activityId && volunteerId1) {
        await enrolVolunteer(activityId, volunteerId1);
      }
      if (activityId && volunteerId2) {
        await enrolVolunteer(activityId, volunteerId2);
      }

      onSuccess();
      handleClose();
    } catch {
      setIsSubmitting(false);
    }
  };

  const ruleSessions = [...rule.sessions].sort((a, b) => a.position - b.position);

  const scheduleSummary =
    rule.actionScheduling === 'regular'
      ? `${rule.actionFrequency.charAt(0).toUpperCase()}${rule.actionFrequency.slice(1)} recurrence`
      : 'Flexible scheduling';

  const renderStep0 = () => (
    <>
      <Box className={classes.header}>
        <Typography className={classes.title}>Application Actions</Typography>
        <IconButton size='small' onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Typography className={classes.subtitle}>
        Choose what to create for this match. Your matching rule defines the sessions, reports and default settings.
      </Typography>

      <Box className={classes.cardList}>
        <Box
          className={`${classes.templateCard} ${selectedTemplate === 'rule' ? classes.templateCardSelected : ''}`}
          onClick={() => setSelectedTemplate('rule')}
        >
          <Box className={`${classes.cardIcon} ${selectedTemplate === 'rule' ? classes.cardIconSelected : ''}`}>
            <svg width='20' height='20' viewBox='0 0 24 24'>
              <path
                d='M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z'
                fill={selectedTemplate === 'rule' ? '#fff' : '#646464'}
              />
            </svg>
          </Box>
          <Box className={classes.cardText}>
            <Typography className={classes.cardTitle}>{rule.name}</Typography>
            <Typography className={classes.cardDesc}>
              {[
                `${ruleSessions.length} session${ruleSessions.length === 1 ? '' : 's'}`,
                scheduleSummary,
                `Runs for ${rule.actionDurationMonths} months`,
              ].join(' · ')}
            </Typography>
          </Box>
        </Box>

        <Box
          className={`${classes.templateCard} ${selectedTemplate === 'blank' ? classes.templateCardSelected : ''}`}
          onClick={() => setSelectedTemplate('blank')}
        >
          <Box className={`${classes.cardIcon} ${selectedTemplate === 'blank' ? classes.cardIconSelected : ''}`}>
            <svg width='20' height='20' viewBox='0 0 24 24'>
              <path d='M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z' fill={selectedTemplate === 'blank' ? '#fff' : '#646464'} />
            </svg>
          </Box>
          <Box className={classes.cardText}>
            <Typography className={classes.cardTitle}>Blank Activity</Typography>
            <Typography className={classes.cardDesc}>
              A single meeting session, with no reports preconfigured
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box className={classes.footer}>
        <Button onClick={handleClose} style={{ color: '#d32f2f', textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant='contained'
          color='primary'
          disabled={!selectedTemplate}
          onClick={handleContinueToStep1}
          style={{ borderRadius: 4, textTransform: 'none', boxShadow: 'none' }}
        >
          Continue
        </Button>
      </Box>
    </>
  );

  const renderStep1 = () => {
    const usingRule = selectedTemplate === 'rule';

    return (
      <>
        <Box className={classes.headerStep1}>
          <Box className={classes.titleStep1}>
            <IconButton size='small' onClick={() => setStep(0)}>
              <ArrowBackIcon fontSize='small' />
            </IconButton>
            Create Match
          </Box>
          <IconButton size='small' onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={classes.step1Content}>
          <Box className={classes.templateBadge}>
            Template:&nbsp;<strong>{usingRule ? rule.name : 'Blank Activity'}</strong>
          </Box>

          <Box mb='20px'>
            <Typography className={classes.fieldLabel}>ACTIVITY NAME</Typography>
            <TextField
              fullWidth
              variant='outlined'
              size='small'
              value={activityName || defaultActivityName}
              onChange={(e) => setActivityName(e.target.value)}
            />
          </Box>

          {eligiblePrograms.length > 1 && (
            <Box mb='20px'>
              <Typography className={classes.fieldLabel}>PROGRAM</Typography>
              <TextField
                fullWidth
                select
                variant='outlined'
                size='small'
                value={selectedProgramId}
                onChange={(e) => setSelectedProgramId(e.target.value)}
                helperText='Both volunteers are in more than one program. Pick where this activity goes.'
              >
                {eligiblePrograms.map((program) => (
                  <MenuItem key={program.programId} value={program.programId}>
                    {program.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}

          <Box mb='20px'>
            <Typography className={classes.fieldLabel}>START DATE</Typography>
            <TextField
              fullWidth
              variant='outlined'
              size='small'
              type='date'
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <Typography className={classes.sectionLabel}>Team</Typography>
          <Box className={classes.teamList}>
            <Box className={classes.teamRow}>
              <Box display='flex' alignItems='center' style={{ gap: 10 }}>
                <FallbackAvatar
                  initials={mentorName
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .slice(0, 2)}
                  size={32}
                />
                <Typography style={{ fontSize: '0.9rem', color: '#545454' }}>{mentorName}</Typography>
              </Box>
              <span className={classes.roleBadge}>{mentorRoleName}</span>
            </Box>
            <Box className={classes.teamRow}>
              <Box display='flex' alignItems='center' style={{ gap: 10 }}>
                <FallbackAvatar
                  initials={participantName
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .slice(0, 2)}
                  size={32}
                />
                <Typography style={{ fontSize: '0.9rem', color: '#545454' }}>{participantName}</Typography>
              </Box>
              <span className={classes.roleBadge}>{participantRoleName}</span>
            </Box>
          </Box>

          <Typography className={classes.sectionLabel}>Scheduling</Typography>
          <Box display='flex' flexDirection='column' style={{ gap: 10, marginBottom: 20 }}>
            <Box
              className={`${classes.radioCard} ${scheduling === 'regular' ? classes.radioCardSelected : ''}`}
              onClick={() => setScheduling('regular')}
            >
              <Box display='flex' style={{ gap: 10 }}>
                <Radio
                  checked={scheduling === 'regular'}
                  size='small'
                  color='primary'
                  style={{ padding: 0, marginTop: 1 }}
                />
                <Box>
                  <Typography style={{ fontWeight: 600, fontSize: '0.9rem', color: '#545454' }}>Regular</Typography>
                  <Typography style={{ fontSize: '0.8rem', color: '#646464', lineHeight: 1.4 }}>
                    Pre-create rosters on a set schedule. Both team members are auto-rostered to each date.
                  </Typography>
                </Box>
              </Box>
              {scheduling === 'regular' && (
                <Box display='flex' style={{ gap: 12, marginTop: 14, marginLeft: 28 }}>
                  <Box flex={1}>
                    <Typography style={{ fontSize: '0.7rem', color: '#646464', marginBottom: 4, fontWeight: 500 }}>
                      FREQUENCY
                    </Typography>
                    <Select
                      fullWidth
                      variant='outlined'
                      value={frequency}
                      onChange={(e) => setFrequency(e.target.value as FrequencyType)}
                      style={{ fontSize: '0.85rem' }}
                      margin='dense'
                    >
                      <MenuItem value='weekly'>Weekly</MenuItem>
                      <MenuItem value='fortnightly'>Fortnightly</MenuItem>
                      <MenuItem value='monthly'>Monthly</MenuItem>
                    </Select>
                  </Box>
                  <Box flex={1}>
                    <Typography style={{ fontSize: '0.7rem', color: '#646464', marginBottom: 4, fontWeight: 500 }}>
                      END DATE
                    </Typography>
                    <TextField
                      fullWidth
                      variant='outlined'
                      size='small'
                      type='date'
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </Box>
                </Box>
              )}
            </Box>

            <Box
              className={`${classes.radioCard} ${scheduling === 'flexible' ? classes.radioCardSelected : ''}`}
              onClick={() => setScheduling('flexible')}
            >
              <Box display='flex' style={{ gap: 10 }}>
                <Radio
                  checked={scheduling === 'flexible'}
                  size='small'
                  color='primary'
                  style={{ padding: 0, marginTop: 1 }}
                />
                <Box>
                  <Typography style={{ fontWeight: 600, fontSize: '0.9rem', color: '#545454' }}>Flexible</Typography>
                  <Typography style={{ fontSize: '0.8rem', color: '#646464', lineHeight: 1.4 }}>
                    No pre-set dates. Mentor or coordinator adds meeting dates via the Rosters page as they go.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {usingRule && ruleSessions.length > 0 && (
            <>
              <Typography className={classes.sectionLabel}>Sessions</Typography>
              <Box className={classes.sessionList}>
                {ruleSessions.map((session, i) => (
                  <Box key={session.matchingRuleSessionId} className={classes.sessionRow}>
                    <Box display='flex' justifyContent='space-between' alignItems='center'>
                      <Box className={classes.sessionName}>
                        <CheckIcon style={{ fontSize: 16, marginRight: 8, color: theme.palette.primary.main }} />
                        {session.name}
                      </Box>
                      {i === 0 && scheduling === 'regular' && <span className={classes.badge}>recurring</span>}
                      {i > 0 && session.isFlexible && <span className={classes.badge}>flexible</span>}
                    </Box>
                    <Typography className={classes.sessionReport}>
                      {session.reportType ? `Report: ${session.reportType.name}` : 'No report'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>

        <Box className={`${classes.footer} ${classes.footerBordered}`}>
          <Button onClick={handleClose} style={{ color: '#d32f2f', textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={handleCreateMatch}
            disabled={isSubmitting}
            style={{ borderRadius: 4, textTransform: 'none', boxShadow: 'none' }}
            startIcon={isSubmitting ? <CircularProgress size={16} color='inherit' /> : undefined}
          >
            {isSubmitting ? 'Creating...' : 'Create Match'}
          </Button>
        </Box>
      </>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} PaperProps={{ className: classes.dialogPaper }} maxWidth={false}>
      {step === 0 ? renderStep0() : renderStep1()}
    </Dialog>
  );
};
